/**
 * Marks cards with a staggered enter animation class.
 * The delay is driven by --stagger-index CSS custom property.
 * The animationend listener cleans up immediately so active CSS animations
 * don't block future opacity transitions (e.g. collapse).
 */
export const markEntering = ($cards: HTMLElement[]): void => {
  $cards.forEach(($card, i) => {
    $card.style.setProperty('--stagger-index', String(i))
    $card.classList.add('is-entering')
    $card.addEventListener(
      'animationend',
      () => {
        $card.classList.remove('is-entering')
        $card.style.removeProperty('--stagger-index')
      },
      { once: true },
    )
  })
}

/** Measures the height the list would have after cardsToHide are removed. */
const measureCollapsedHeight = ($list: HTMLElement, $cardsToHide: HTMLElement[]): number => {
  $cardsToHide.forEach(($c) => $c.setAttribute('hidden', ''))
  const height = $list.scrollHeight
  $cardsToHide.forEach(($c) => $c.removeAttribute('hidden'))
  return height
}

/** Locks the list at a fixed pixel height so it can be transitioned. */
const lockList = ($list: HTMLElement, height: number): void => {
  $list.style.height = `${height}px`
  $list.style.overflow = 'hidden'
}

/** Removes all inline styles added during collapse and strips CSS classes from cards. */
const releaseList = ($list: HTMLElement, $cards: HTMLElement[]): void => {
  $list.style.height = ''
  $list.style.overflow = ''
  $list.style.transition = ''
  $cards.forEach(($c) => $c.classList.remove('is-collapsing'))
}

/**
 * Fades out cards (via CSS .is-collapsing) and collapses the offersList
 * container height simultaneously, then calls onDone.
 */
export const animateCollapseAndRender = (
  $offersList: HTMLElement,
  $cardsToHide: HTMLElement[],
  onDone: () => void,
): void => {
  if ($cardsToHide.length === 0) {
    onDone()
    return
  }

  const currentHeight = $offersList.scrollHeight
  const targetHeight = measureCollapsedHeight($offersList, $cardsToHide)

  lockList($offersList, currentHeight)
  $cardsToHide.forEach(($c) => $c.classList.add('is-collapsing'))

  // Force layout — browser must commit currentHeight before we animate to targetHeight
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  $offersList.offsetHeight

  $offersList.style.transition = 'height 0.4s ease'
  $offersList.style.height = `${targetHeight}px`

  const onTransitionEnd = (e: TransitionEvent): void => {
    if (e.propertyName !== 'height') return
    $offersList.removeEventListener('transitionend', onTransitionEnd)
    releaseList($offersList, $cardsToHide)
    onDone()
  }

  $offersList.addEventListener('transitionend', onTransitionEnd)
}
